"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, FileText, MessageSquare } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const keyPoints = [{
  title: "Application Feasibility",
  description: "Sample testing and proof-of-concept verification for your specific materials."
}, {
  title: "Regulatory Compliance",
  description: "Guidance on qualification, data integrity, and pharmacopoeia alignment."
}, {
  title: "Instrument Sizing & Setup",
  description: "Hardware configuration tailored to laboratory workflows or in-line process points."
}];
export default function DiscussCTA() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="discussapplicationcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="page-container">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="relative overflow-hidden rounded-2xl bg-secondary/60 border border-border p-8 md:p-12 lg:p-16 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left Column: Context & Details */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="bg-background text-foreground border-border px-3 py-1">
                  Application Consultation
                </Badge>
                <span className="text-xs text-muted-foreground">Ireland & UK Support</span>
              </motion.div>

              <motion.div variants={itemVariants} className="space-y-3">
                <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
                  Have a specific analytical challenge in mind?
                </h2>
                <p className="font-default text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                  Talk through your process requirements with our application specialists. We help you assess sample feasibility, instrument fit, and regulatory validation before you commit.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                {keyPoints.map((point, index) => <div key={point.title} data-index={index} className="flex flex-col gap-1.5 p-3.5 rounded-lg bg-background/80 border border-border/80">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-primary shrink-0" />
                      <span className="font-default text-sm font-semibold text-foreground">
                        {point.title}
                      </span>
                    </div>
                    <p className="font-default text-xs text-muted-foreground leading-normal">
                      {point.description}
                    </p>
                  </div>)}
              </motion.div>
            </div>

            {/* Right Column: Interactive Card */}
            <div className="lg:col-span-5">
              <motion.div variants={itemVariants}>
                <Card className="bg-card text-card-foreground border-border shadow-md rounded-xl p-6 sm:p-8 flex flex-col gap-6">
                  <div className="flex items-start gap-4">
                    <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <MessageSquare className="size-5" />
                    </div>
                    <div>
                      <h3 className="font-default text-lg font-semibold text-foreground">
                        Request a Technical Consultation
                      </h3>
                      <p className="font-default text-sm text-muted-foreground mt-1">
                        Direct review of your analytical requirements, methods, or site installation.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-border">
                    <Button asChild size="lg" className="w-full justify-between group">
                      <Link to="/DiscussYourApplicationPage">
                        <span>Discuss Your Application</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>

                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <Button asChild variant="outline" size="sm" className="w-full">
                        <Link to="/BookADemoPage">
                          <SafeHydrate><Calendar className="size-3.5 mr-1" /></SafeHydrate>
                          <span>Book a Demo</span>
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="sm" className="w-full">
                        <Link to="/FeasibilityStudiesPage">
                          <FileText className="size-3.5 mr-1" />
                          <span>Feasibility</span>
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <p className="font-default text-xs text-center text-muted-foreground">
                    Regulated laboratory and manufacturing support across Ireland and the UK.
                  </p>
                </Card>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}